import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { PiWarningCircleLight } from "react-icons/pi";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import ClientAuthContext from "../../context/ClientAuthContext";
import { serverInstance } from "../../service/axios";
import MeetingPayment from "./MeetingPayment";
import { formatAmount, formatPricingDate, getPricingForTier, getPricingPreview, getTierLabel, hasMemberPricingAccess } from "./meetingPricing";
import {
  buildMeetingRegisterLocation,
  buildMeetingRegisterSearchParams,
  encodeAttendeeIdForUrl,
  readAttendeeIdFromLocation,
  requiresMeetingLogin,
} from "./attendeeUrlUtils";
import "./meetingRegister.scss";

const DISABLED_ABSTRACT_SUBMISSION_FIELDS = [
  "abstract",
  "abstract_title",
  "abstract_text",
  "abstract_file",
  "abstract_file_url",
  "abstract_url",
  "presentation_abstract",
  "presentation_file",
  "presentation_file_url",
  "presentation_summary",
  "presentation_title"
];

const getAuthHeaders = (token) => ({
  Authorization: `Bearer ${token}`,
  "Content-Type": "application/json",
});

const isPaidMeeting = (meetingItem) => (
  meetingItem?.paid === true ||
  !!meetingItem?.paid_at ||
  String(meetingItem?.payment_status || "").toLowerCase() === "paid"
);

const MeetingRegister = ({ data, language }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const meetingId = searchParams.get("meeting_id")?.trim().replace(/:+$/, "") || "1775628421050";
  const requiresLogin = requiresMeetingLogin(searchParams);
  const attendeeId = useMemo(
    () => (requiresLogin ? "" : readAttendeeIdFromLocation({ search: location.search })),
    [location.search, requiresLogin]
  );
  const hasAuthToken = !!localStorage.getItem("msidclienttoken");
  const { userData } = useContext(ClientAuthContext);
  const isLoggedInUser = !!userData?.id;
  const isGuestRegistration = !requiresLogin;
  const isNewGuestEntry = isGuestRegistration && !attendeeId && !isLoggedInUser && !hasAuthToken;
  const showGuestRegistrationLabel = isGuestRegistration && !!attendeeId;
  const isAuthenticating = hasAuthToken && !userData?.id && (requiresLogin || !attendeeId);
  const memberRegisterFrom = useMemo(
    () => buildMeetingRegisterLocation({ meetingId, requireLogin: true }),
    [meetingId]
  );
  const [registered, setRegistered] = useState(false);
  const [paid, setPaid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [syncingDetails, setSyncingDetails] = useState(false);
  const [meetingLoading, setMeetingLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [meetingInfo, setMeetingInfo] = useState(null);
  const [savedResident, setSavedResident] = useState(null);
  const [meetingInvoiceId, setMeetingInvoiceId] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [appliedCouponCode, setAppliedCouponCode] = useState("");
  const [appliedCouponPercent, setAppliedCouponPercent] = useState(0);
  const [couponMessage, setCouponMessage] = useState("");
  const [couponMessageType, setCouponMessageType] = useState("");
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const normalizedCouponCode = useMemo(() => couponCode.trim().toUpperCase(), [couponCode]);
  const memberPricing = useMemo(() => getPricingForTier({ meetingInfo, tier: "member" }), [meetingInfo]);
  const standardPricing = useMemo(() => getPricingForTier({ meetingInfo, tier: "standard" }), [meetingInfo]);
  const residentPricing = useMemo(() => getPricingForTier({ meetingInfo, tier: "resident" }), [meetingInfo]);
  const publicPricingRows = [standardPricing, memberPricing, residentPricing].filter(Boolean);
  const loggedInUnpaidPricingRows = publicPricingRows;
  const [formData, setFormData] = useState({
    last_name: "",
    first_name: "",
    occupation: "",
    workplace: "",
    mobile_number: "",
    email: "",
    resident: false,
    job: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrorMsg("");
    setSuccessMsg("");
  };

  const handleResidentChange = (e) => {
    setFormData((prev) => ({ ...prev, resident: e.target.checked }));
    setErrorMsg("");
    setSuccessMsg("");
  };

  const persistMeetingCoupon = useCallback((code, percent) => {
    const token = localStorage.getItem("msidclienttoken");
    const resolvedAttendeeId = isGuestRegistration ? (attendeeId || formData.email.trim().toLowerCase()) : "";
    if (!registered || (isGuestRegistration && !resolvedAttendeeId)) {
      return Promise.resolve();
    }

    const payload = {
      meeting_id: meetingId,
      coupon_code: code,
      coupon_percent: percent,
    };
    if (isGuestRegistration && resolvedAttendeeId) {
      payload.attendee_id = resolvedAttendeeId;
    }

    const requestConfig = token && !isGuestRegistration ? { headers: getAuthHeaders(token) } : {};
    return serverInstance.post("/client/attendee/meeting-coupon", payload, requestConfig);
  }, [attendeeId, formData.email, isGuestRegistration, meetingId, registered]);

  const handleCouponChange = (e) => {
    const hadAppliedCoupon = !!appliedCouponCode;
    setCouponCode(e.target.value.toUpperCase());
    setAppliedCouponCode("");
    setAppliedCouponPercent(0);
    setCouponMessage("");
    setCouponMessageType("");
    setErrorMsg("");
    setSuccessMsg("");

    if (hadAppliedCoupon && registered) {
      persistMeetingCoupon("", 0);
    }
  };

  const applyCouponCode = () => {
    if (!normalizedCouponCode) {
      setAppliedCouponCode("");
      setAppliedCouponPercent(0);
      setCouponMessageType("error");
      setCouponMessage(data.invalidCouponCode || (language ? "Invalid coupon code" : "Купон код буруу байна"));
      return;
    }

    const token = localStorage.getItem("msidclienttoken");
    const guestEmail = formData.email.trim().toLowerCase();
    const isGuest = isGuestRegistration;

    if (!isGuest && !token) {
      setAppliedCouponCode("");
      setAppliedCouponPercent(0);
      setCouponMessageType("error");
      setCouponMessage(data.couponLoginRequired || (language ? "Please sign in to use coupon" : "Купон ашиглахын тулд нэвтэрнэ үү"));
      return;
    }

    setApplyingCoupon(true);
    setCouponMessage("");
    setCouponMessageType("");
    setErrorMsg("");
    setSuccessMsg("");

    const couponPayload = {
      meeting_id: meetingId,
      coupon_code: normalizedCouponCode,
      ...(isGuest && guestEmail ? { email: guestEmail } : {}),
    };
    const couponRequestConfig = token && !isGuest ? { headers: getAuthHeaders(token) } : {};

    serverInstance.post("/client/invoice/meeting/coupon", couponPayload, couponRequestConfig)
      .then((res) => {
        const appliedCode = String(res?.data?.coupon_code || normalizedCouponCode).trim().toUpperCase();
        const appliedPercent = Number(res?.data?.coupon_percent);
        setCouponCode(appliedCode);
        setAppliedCouponCode(appliedCode);
        setAppliedCouponPercent(Number.isFinite(appliedPercent) ? appliedPercent : 0);
        setCouponMessageType("success");
        setCouponMessage(data.couponApplied || (language ? "Successfully applied code" : "Купон код амжилттай"));

        if (registered) {
          return persistMeetingCoupon(
            appliedCode,
            Number.isFinite(appliedPercent) ? appliedPercent : 0
          );
        }
        return null;
      })
      .catch((err) => {
        const responseData = err?.response?.data;
        const responseMessage = String(responseData?.message || (typeof responseData === "string" ? responseData : "")).toLowerCase();
        setAppliedCouponCode("");
        setAppliedCouponPercent(0);
        setCouponMessageType("error");
        if (err?.response?.status === 401) {
          setCouponMessage(data.couponLoginRequired || (language ? "Please sign in to use coupon" : "Купон ашиглахын тулд нэвтэрнэ үү"));
        }
        else if (responseMessage.includes("limit")) {
          setCouponMessage(data.couponLimitReached || (language ? "Coupon code limit reached" : "Купон кодын хязгаар дууссан байна"));
        }
        else {
          setCouponMessage(data.invalidCouponCode || (language ? "Invalid coupon code" : "Купон код буруу байна"));
        }
      })
      .finally(() => setApplyingCoupon(false));
  };

  const getRegistrationPayload = () => {
    const payload = {
      ...formData,
      email: formData.email.toLowerCase(),
      meeting_id: meetingId
    };

    if (appliedCouponCode && appliedCouponPercent > 0) {
      payload.coupon_code = appliedCouponCode;
      payload.coupon_percent = appliedCouponPercent;
    }

    // Abstract submission is closed; do not send legacy abstract fields.
    DISABLED_ABSTRACT_SUBMISSION_FIELDS.forEach((field) => {
      delete payload[field];
    });

    return payload;
  };

  const syncProfileDetailsToForm = (profile) => {
    setFormData((prev) => ({
      ...prev,
      last_name: profile?.last_name || "",
      first_name: profile?.first_name || "",
      occupation: profile?.occupation || "",
      workplace: profile?.workplace || "",
      mobile_number: profile?.mobile_number || "",
      email: profile?.email || "",
      job: profile?.job || "",
    }));
  };

  const applyAttendeeToState = useCallback((attendee, targetMeetingId = meetingId) => {
    if (!attendee?.email) {
      return;
    }

    const meetingItem = (attendee.attend_meetings || []).find((item) => String(item?.meeting_id) === String(targetMeetingId));
    const attendeeMeetingMatches = String(attendee.meeting_id || "") === String(targetMeetingId);
    const isRegisteredForMeeting = !!meetingItem || attendeeMeetingMatches;
    const isPaidForMeeting = isPaidMeeting(meetingItem) || (attendeeMeetingMatches && isPaidMeeting(attendee));

    setRegistered(isRegisteredForMeeting);
    setPaid(isPaidForMeeting);
    setSavedResident(isRegisteredForMeeting ? !!(meetingItem || attendee)?.resident : null);
    setMeetingInvoiceId(String(meetingItem?.invoice_id || (attendeeMeetingMatches ? attendee.invoice_id : "") || "").trim());
    setFormData((prev) => ({
      ...prev,
      last_name: attendee.last_name || prev.last_name,
      first_name: attendee.first_name || prev.first_name,
      occupation: meetingItem?.occupation || attendee.occupation || prev.occupation,
      workplace: meetingItem?.workplace || attendee.workplace || prev.workplace,
      mobile_number: attendee.mobile_number || prev.mobile_number,
      email: attendee.email || prev.email,
      job: meetingItem?.job || attendee.job || prev.job,
      resident: isRegisteredForMeeting ? !!(meetingItem || attendee)?.resident : prev.resident,
    }));

    const storedCouponCode = String(meetingItem?.coupon_code || "").trim().toUpperCase();
    const storedCouponPercent = Number(meetingItem?.coupon_percent);
    if (storedCouponCode && Number.isFinite(storedCouponPercent) && storedCouponPercent > 0) {
      setCouponCode(storedCouponCode);
      setAppliedCouponCode(storedCouponCode);
      setAppliedCouponPercent(storedCouponPercent);
    } else {
      setCouponCode("");
      setAppliedCouponCode("");
      setAppliedCouponPercent(0);
    }
  }, [meetingId]);

  const syncDetails = () => {
    const token = localStorage.getItem("msidclienttoken");
    if (!token) {
      return;
    }

    setSyncingDetails(true);
    setErrorMsg("");
    setSuccessMsg("");

    serverInstance.post("/client", {}, {
      headers: getAuthHeaders(token),
    })
      .then((res) => {
        syncProfileDetailsToForm(res?.data?.data || {});
        setSuccessMsg(data.detailsSynced || (language ? "Details synced" : "Мэдээлэл шинэчлэгдлээ"));
      })
      .catch((err) => {
        setErrorMsg(
          err?.response?.data?.message || data.error || (language ? "An error occurred" : "Алдаа гарлаа")
        );
      })
      .finally(() => setSyncingDetails(false));
  };

  const getAttendee = useCallback(() => {
    setLoading(true);
    setRegistered(false);
    setPaid(false);
    setSavedResident(null);
    setMeetingInvoiceId("");
    const token = localStorage.getItem("msidclienttoken");
    serverInstance
      .get("/client/attendee", {
        headers: getAuthHeaders(token),
      })
      .then((res) => {
        applyAttendeeToState(res?.data?.data, meetingId);
      })
      .catch((err) => {
        setErrorMsg(
          err?.response?.data?.message || data.error || (language ? "An error occurred" : "Алдаа гарлаа")
        );
      })
      .finally(() => setLoading(false));
  }, [applyAttendeeToState, data.error, language, meetingId]);

  const loadGuestAttendee = useCallback(() => {
    if (!attendeeId || !meetingId) {
      return;
    }

    setLoading(true);
    setErrorMsg("");

    serverInstance
      .get("/client/attendee", {
        params: {
          attendee_id: attendeeId,
          meeting_id: meetingId,
        },
      })
      .then((res) => {
        applyAttendeeToState(res?.data?.data, meetingId);
      })
      .catch((err) => {
        if (err?.response?.status !== 404) {
          setErrorMsg(
            err?.response?.data?.message || data.error || (language ? "An error occurred" : "Алдаа гарлаа")
          );
        }
      })
      .finally(() => setLoading(false));
  }, [applyAttendeeToState, attendeeId, data.error, language, meetingId]);

  const handleMeetingPaid = useCallback(() => {
    setRegistered(true);
    setPaid(true);
    setMeetingInvoiceId("");
    setErrorMsg("");
    setSuccessMsg("");
  }, []);

  const handleMeetingInvoiceChange = useCallback((/** @type {string} */ nextInvoiceId) => {
    setMeetingInvoiceId(String(nextInvoiceId || "").trim());
  }, []);

  const submit = (e) => {
    e.preventDefault();

    if (!meetingId) {
      setErrorMsg(language ? "Meeting is not selected" : "Хурал сонгогдоогүй байна");
      return;
    }

    if (!validate()) return;

    setLoading(true);
    const token = isGuestRegistration ? "" : localStorage.getItem("msidclienttoken");
    const payload = getRegistrationPayload();
    const wasRegistered = registered;
    const request = serverInstance.post("/client/attendee", payload, {
      headers: token ? getAuthHeaders(token) : { "Content-Type": "application/json" },
    });

    request
      .then((res) => {
        const savedAttendeeId = String(res?.data?.data?.id || formData.email.trim().toLowerCase());
        if (isGuestRegistration) {
          setSearchParams(buildMeetingRegisterSearchParams({
            meetingId,
            attendeeId: savedAttendeeId,
          }), { replace: true });
        } else {
          setSearchParams(buildMeetingRegisterSearchParams({
            meetingId,
            requireLogin: true,
          }), { replace: true });
        }
        setRegistered(true);
        setSavedResident(!!payload.resident);
        setErrorMsg("");
        if (isGuestRegistration) {
          setSuccessMsg(
            wasRegistered
              ? (data.registrationSaved || (language ? "Registration updated" : "Бүртгэлийн мэдээлэл хадгалагдлаа"))
              : (data.guestRecoveryEmailSent || (language ? "Registration saved. A recovery link was sent to your email." : "Бүртгэл хадгалагдлаа. Имэйл хаяг руу сэргээх холбоос илгээгдлээ."))
          );
        }
        else {
          setSuccessMsg(wasRegistered ? (data.registrationSaved || (language ? "Registration updated" : "Бүртгэлийн мэдээлэл хадгалагдлаа")) : "");
        }
      })
      .catch((err) => {
        if (err?.response?.status === 409) {
          const alreadyPaid = !!err.response?.data?.paid;
          setErrorMsg("");
          setRegistered(true);
          setPaid(alreadyPaid);
          if (alreadyPaid) {
            setSuccessMsg(language ? "You have already registered" : "Аль хэдийн бүртгүүлсэн байна")
          } else {
            setSuccessMsg("");
            setErrorMsg(data.registrationUpdateFailed || (language ? "Could not update registration" : "Бүртгэлийн мэдээлэл хадгалж чадсангүй"));
          }
        }
        else {
          setErrorMsg(err?.response?.data?.message || data.error || (language ? "An error occurred" : "Алдаа гарлаа"))
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    window.scrollTo(0,0)
  }, []);

  useEffect(() => {
    if (!meetingId) {
      return;
    }
    setMeetingLoading(true);
    serverInstance.get("/app/meetings")
      .then((res) => {
        const items = res?.data?.items || [];
        const selectedMeeting = items.find((item) => String(item?.id) === String(meetingId));
        setMeetingInfo(selectedMeeting || null);
      })
      .catch(() => {
        setMeetingInfo(null);
      })
      .finally(() => setMeetingLoading(false));
  }, [meetingId]);

  useEffect(() => {
    if (requiresLogin && !hasAuthToken) {
      navigate("/client/signin", { state: { from: location }, replace: true });
    }
  }, [hasAuthToken, location, navigate, requiresLogin]);

  useEffect(() => {
    if (requiresLogin && !userData?.id) {
      return;
    }

    if (attendeeId) {
      const rawAttendeeParam = searchParams.get("attendee_id") || "";
      const expectedEncodedAttendeeId = encodeAttendeeIdForUrl(attendeeId);
      const needsUrlNormalization = rawAttendeeParam.includes("@")
        || (expectedEncodedAttendeeId && rawAttendeeParam !== expectedEncodedAttendeeId);

      if (needsUrlNormalization) {
        setSearchParams(buildMeetingRegisterSearchParams({ meetingId, attendeeId }), { replace: true });
        return;
      }

      loadGuestAttendee();
      return;
    }

    if (userData?.id) {
      setFormData({
        last_name: userData.last_name || "",
        first_name: userData.first_name || "",
        occupation: userData.occupation || "",
        workplace: userData.workplace || "",
        mobile_number: userData.mobile_number || "",
        email: userData.email || "",
        job: userData.job || "",
        resident: false,
      });

      if (!requiresLogin) {
        setSearchParams(buildMeetingRegisterSearchParams({
          meetingId,
          requireLogin: true,
        }), { replace: true });
        return;
      }

      getAttendee();
    }
  }, [attendeeId, getAttendee, loadGuestAttendee, hasAuthToken, location.search, meetingId, requiresLogin, searchParams, setSearchParams, userData?.id]);

  const validate = () => {
    const mobilePattern = /^[0-9]{8}$/;
    if (!formData.last_name.trim()) {
      setErrorMsg(data.last_nameRequired || (language ? "Please enter last name" : "Овог оруулна уу"));
      return false;
    }
    if (!formData.first_name.trim()) {
      setErrorMsg(data.first_nameRequired || (language ? "Please enter first name" : "Нэр оруулна уу"));
      return false;
    }
    if (!formData.occupation.trim()) {
      setErrorMsg(data.occupationRequired || (language ? "Please enter profession" : "Төрөлжсөн мэргэжил оруулна уу"));
      return false;
    }
    if (!formData.workplace.trim()) {
      setErrorMsg(data.workplaceRequired || (language ? "Please enter workplace" : "Ажлын газар оруулна уу"));
      return false;
    }
    if (!mobilePattern.test(formData.mobile_number)) {
      setErrorMsg(data.invalidMobile || (language ? "Invalid mobile number" : "Утасны дугаар буруу байна"));
      return false;
    }
    if (
      !formData.email.trim() ||
      !/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email)
    ) {
      setErrorMsg(data.invalidEmail || (language ? "Invalid email" : "Имэйл буруу байна"));
      return false;
    }
    return true;
  };

  const membershipActive = isGuestRegistration ? false : hasMemberPricingAccess(userData);
  const paidUntilValue = typeof userData?.paid_until === "string"
    ? userData.paid_until.trim()
    : userData?.paid_until;
  const clientMembershipExpiredOrEmpty = !isGuestRegistration && !!userData?.id && (!paidUntilValue || !membershipActive);
  const canShowCoupon = !!meetingInfo && !paid;
  const selectedPricingPreview = useMemo(
    () => getPricingPreview({ meetingInfo, resident: formData.resident, membershipActive }),
    [meetingInfo, formData.resident, membershipActive]
  );
  const couponPricingPreview = useMemo(() => {
    if (!selectedPricingPreview || !appliedCouponCode || !appliedCouponPercent) {
      return null;
    }

    const previewAmount = Number(selectedPricingPreview.amount);
    if (!Number.isFinite(previewAmount)) {
      return null;
    }

    const couponDiscountAmount = Math.max(
      0,
      Math.min(previewAmount, Math.round(previewAmount * appliedCouponPercent / 100))
    );

    return {
      ...selectedPricingPreview,
      amount: Math.max(0, previewAmount - couponDiscountAmount),
      coupon_code: appliedCouponCode,
      coupon_discount_amount: couponDiscountAmount,
      coupon_percent: appliedCouponPercent,
    };
  }, [selectedPricingPreview, appliedCouponCode, appliedCouponPercent]);
  const paymentCouponCode = appliedCouponCode;
  const paymentCouponPercent = appliedCouponPercent;
  const submitButtonText = loading
    ? data.loading || (language ? "Please wait..." : "Түр хүлээнэ үү...")
    : registered
      ? paid
        ? (language ? "Registered" : "Бүртгүүлсэн")
        : data.save || (language ? "Save" : "Хадгалах")
      : (language ? "Register for meeting" : "Хуралд бүртгүүлэх");

  const renderCouponField = () => (
    <div className="form-div coupon-field">
      <label className="sign-label" htmlFor="meeting-coupon">
        {language ? "Coupon code" : "Купон код"}
      </label>
      <div className="coupon-input-row">
        <input
          id="meeting-coupon"
          type="text"
          name="coupon_code"
          value={couponCode}
          onChange={handleCouponChange}
          autoCapitalize="characters"
          className="sign-input"
          disabled={paid}
        />
        <button
          type="button"
          className="coupon-apply-button"
          disabled={paid || applyingCoupon || !normalizedCouponCode || normalizedCouponCode === appliedCouponCode}
          onClick={applyCouponCode}
        >
          {applyingCoupon ? (data.loading || (language ? "Please wait..." : "Түр хүлээнэ үү...")) : (language ? "Apply" : "Хэрэглэх")}
        </button>
      </div>
      {couponMessage ? (
        <div className={`coupon-message ${couponMessageType === "success" ? "coupon-success" : "coupon-error"}`}>
          {couponMessage}
        </div>
      ) : null}
      {!registered && couponPricingPreview ? (
        <div className="coupon-pricing-preview">
          <div className="pricing-line">
            <span>{language ? "Estimated registration fee" : "Төлөх бүртгэлийн төлбөр"}</span>
            <strong>{formatAmount(couponPricingPreview.amount, couponPricingPreview.currency)}</strong>
          </div>
          {couponPricingPreview.tier ? (
            <div className="pricing-meta">
              {language ? "Type" : "Төрөл"}: {getTierLabel(couponPricingPreview.tier, language)}
              {!registered ? (
                <span className="pricing-note">
                  {" "}
                  ({language ? "updates when resident option changes" : "резидент эмч сонголтоор өөрчлөгдөнө"})
                </span>
              ) : null}
            </div>
          ) : null}
          {Number(couponPricingPreview.coupon_discount_amount) > 0 ? (
            <div className="pricing-discount">
              {language ? "Coupon" : "Купон"} {appliedCouponCode}: -{formatAmount(couponPricingPreview.coupon_discount_amount, couponPricingPreview.currency)}
            </div>
          ) : null}
          {Number(selectedPricingPreview?.amount) > Number(couponPricingPreview.amount) ? (
            <div className="pricing-meta">
              {language ? "Before coupon" : "Купонгүй"}: {formatAmount(selectedPricingPreview.amount, selectedPricingPreview.currency)}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );

  if (requiresLogin && !hasAuthToken) {
    return null;
  }

  if (isAuthenticating) {
    return (
      <div className="sign-container">
        <div className="sign-container-card">
          <h2>{language ? "Registration" : "Хурлын бүртгэл"}</h2>
          <div className="form-div">{language ? "Loading..." : "Түр хүлээнэ үү..."}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="sign-container">
      <div className="sign-container-card">
        <div>
          <h2>{language ? "Registration" : "Хурлын бүртгэл"}</h2>
          {meetingLoading ? (
            <div className="form-div">{language ? "Loading meeting..." : "Хурлын мэдээлэл уншиж байна..."}</div>
          ) : null}
          {meetingInfo ? (
            <div className="form-div">
              <strong>{language ? "Meeting" : "Хурал"}:</strong>{" "}
              {language ? (meetingInfo.title_en && meetingInfo.title_en.trim() ? meetingInfo.title_en : meetingInfo.title) : meetingInfo.title}
              <br />
              <strong>{language ? "Date" : "Огноо"}:</strong>{" "}
              {meetingInfo.dt ? new Date(meetingInfo.dt).toLocaleString(language ? "en-US" : "mn-MN") : "-"}
              <br />
              <strong>{language ? "Location" : "Байршил"}:</strong>{" "}
              {meetingInfo.location || "-"}
            </div>
          ) : null}

          {isGuestRegistration ? (
            <>
              {publicPricingRows.length > 0 ? (
                <div className="meeting-pricing">
                  {publicPricingRows.map((row) => {
                    const hasActiveEarlyBirdDeal = Number(row.discount_amount) > 0
                      && Number(row.original_amount) > Number(row.amount);
                    const earlyBirdEndDate = formatPricingDate(row.discount_ends_at, language);

                    return (
                      <div className={"pricing-card" + (hasActiveEarlyBirdDeal ? " has-early-bird" : "")} key={row.tier}>
                        <div className="pricing-type">{getTierLabel(row.tier, language)}</div>
                        {hasActiveEarlyBirdDeal ? (
                          <>
                            <div className="pricing-discount">
                              {earlyBirdEndDate ? (
                                <div className="pricing-end-date">
                                  {language ? "Early bird ends" : "Эрт бүртгэл дуусах"}: {earlyBirdEndDate}
                                </div>
                              ) : null}
                            </div>
                            <div className="pricing-amount pricing-amount-early">
                              {formatAmount(row.amount, row.currency)}
                            </div>
                            <div className="pricing-original pricing-original-striked">
                              {formatAmount(row.original_amount, row.currency)}
                            </div>
                          </>
                        ) : (
                          <div className="pricing-amount">{formatAmount(row.amount, row.currency)}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : null}
              {isGuestRegistration && !attendeeId && memberPricing ? (
                <>
                  <div className="form-div member-price-reminder">
                    <PiWarningCircleLight aria-hidden="true" />
                    <span>
                      {data.memberPriceLoginReminder || (language
                        ? "Login or sign up to get member price, or register as a guest below."
                        : "Гишүүний үнээр бүртгүүлэхийн тулд нэвтэрнэ үү эсвэл бүртгүүлнэ үү. Эсвэл доорх маягтаар зочиноор бүртгүүлнэ үү.")}
                    </span>
                  </div>
                  <div className="member-price-link-row">
                    <Link className="member-price-link" to="/signup" state={{ from: memberRegisterFrom }}>
                      {data.signupToRegister || data.signup || (language ? "Sign up and register" : "Бүртгүүлээд хуралд бүртгүүлэх")}
                    </Link>
                    <span className="member-price-link-sep" aria-hidden="true">·</span>
                    <Link className="member-price-link" to="/client/signin" state={{ from: memberRegisterFrom }}>
                      {data.loginToRegister || data.signin || (language ? "Login and register" : "Нэвтэрч хуралд бүртгүүлэх")}
                    </Link>
                  </div>
                </>
              ) : isNewGuestEntry ? (
                <div className="form-div member-price-reminder">
                  <PiWarningCircleLight aria-hidden="true" />
                  <span>
                    {data.guestRegisterHint || (language
                      ? "You can register as a guest without creating an account."
                      : "Та бүртгэл үүсгэхгүйгээр зочиноор бүртгүүлэх боломжтой.")}
                  </span>
                </div>
              ) : null}
            </>
          ) : null}

          {clientMembershipExpiredOrEmpty && loggedInUnpaidPricingRows.length > 0 && !paid ? (
            <div className="meeting-pricing">
              {loggedInUnpaidPricingRows.map((row) => {
                const hasActiveEarlyBirdDeal = Number(row.discount_amount) > 0
                  && Number(row.original_amount) > Number(row.amount);
                const earlyBirdEndDate = formatPricingDate(row.discount_ends_at, language);

                return (
                  <div className={"pricing-card" + (hasActiveEarlyBirdDeal ? " has-early-bird" : "")} key={row.tier}>
                    <div className="pricing-type">{getTierLabel(row.tier, language)}</div>
                    {hasActiveEarlyBirdDeal ? (
                      <>
                        <div className="pricing-discount">
                          {earlyBirdEndDate ? (
                            <div className="pricing-end-date">
                              {language ? "Early bird ends" : "Эрт бүртгэл дуусах"}: {earlyBirdEndDate}
                            </div>
                          ) : null}
                        </div>
                        <div className="pricing-amount pricing-amount-early">
                          {formatAmount(row.amount, row.currency)}
                        </div>
                        <div className="pricing-original pricing-original-striked">
                          {formatAmount(row.original_amount, row.currency)}
                        </div>
                      </>
                    ) : (
                      <div className="pricing-amount">{formatAmount(row.amount, row.currency)}</div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : null}

          {clientMembershipExpiredOrEmpty && memberPricing && !paid ? (
            <>
              <div className="form-div member-price-reminder">
                <PiWarningCircleLight aria-hidden="true" />
                <span>{data.memberPriceReminder || "Buy membership to get member price"}</span>
              </div>
              <div className="member-price-link-row">
                <Link className="member-price-link" to="/membership">
                  {data.memberSignupLink || "Become a member"}
                </Link>
              </div>
            </>
          ) : null}

          {canShowCoupon ? renderCouponField() : null}

          {registered ?
            paid ? <div className="success">{language ? "Payment completed" : "Төлбөр төлөгдсөн байна"}</div>
            :
            savedResident === formData.resident ? (
              <MeetingPayment
                key={`${meetingId}-${formData.resident ? "resident" : "standard"}-${membershipActive ? "member" : "nonmember"}-${paymentCouponCode || "no-coupon"}`}
                email={formData.email}
                resident={formData.resident}
                meetingId={meetingId}
                meetingInfo={meetingInfo}
                membershipActive={membershipActive}
                attendeeId={isGuestRegistration ? (attendeeId || formData.email.trim().toLowerCase()) : ""}
                initialInvoiceId={meetingInvoiceId}
                couponCode={paymentCouponCode}
                couponPercent={paymentCouponPercent}
                language={language}
                onPaid={handleMeetingPaid}
                onInvoiceChange={handleMeetingInvoiceChange}
              />
            ) : savedResident !== null ? (
              <div className="form-div member-price-reminder">
                <PiWarningCircleLight aria-hidden="true" />
                <span>{data.saveBeforePayment || (language ? "Save changes before payment" : "Төлбөр төлөхөөс өмнө өөрчлөлтөө хадгална уу")}</span>
              </div>
            ) : null : null
          }

          {successMsg && <div className="success">{successMsg}</div>}

          {showGuestRegistrationLabel ? (
            <div className="guest-registration-label">
              {data.guestRegistrationLabel || (language ? "Guest registration" : "Зочны бүртгэл")}
            </div>
          ) : null}

          <form onSubmit={submit}>
            {!isGuestRegistration && userData?.id ?
            <div className="form-div">
              {language ? 
                <>You can change the information below from the <Link className="my-link" to="/client">My Information</Link> section.</> :
                <>Та доорх мэдээллийг <Link className="my-link" to="/client">Миний мэдээлэл</Link> хэсгээс өөрчлөх боломжтой.</>
              }
              <button
                type="button"
                className="sync-details-button"
                disabled={syncingDetails || loading}
                onClick={syncDetails}
              >
                {syncingDetails
                  ? data.loading || (language ? "Please wait..." : "Түр хүлээнэ үү...")
                  : data.syncDetails || (language ? "Sync details" : "Мэдээлэл шинэчлэх")}
              </button>
            </div>
            : null}
            <div className="form-div">
              <label className="sign-label">{data.lname}</label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
                className="sign-input"
                disabled={(!isGuestRegistration && userData.id) || paid}
              />
            </div>
            <div className="form-div">
              <label className="sign-label">{data.fname}</label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                className="sign-input"
                disabled={(!isGuestRegistration && userData.id) || paid}
              />
            </div>
            <div className="form-div">
              <label className="sign-label">{data.occupation}</label>
              <input
                type="text"
                name="occupation"
                value={formData.occupation}
                onChange={handleChange}
                required
                className="sign-input"
                disabled={paid}
              />
            </div>
            <div className="form-div">
              <label className="sign-label">{data.job}</label>
              <input
                type="text"
                name="job"
                value={formData.job}
                onChange={handleChange}
                required
                className="sign-input"
                disabled={paid}
              />
            </div>
            <div className="form-div">
              <label className="sign-label">{data.workplace}</label>
              <input
                type="text"
                name="workplace"
                value={formData.workplace}
                onChange={handleChange}
                required
                className="sign-input"
                disabled={paid}
              />
            </div>
            <div className="form-div">
              <label className="sign-label">{data.mnumber}</label>
              <input
                type="tel"
                name="mobile_number"
                pattern="[0-9]{8}"
                value={formData.mobile_number}
                onChange={handleChange}
                required
                className="sign-input"
                disabled={(!isGuestRegistration && userData.id) || paid}
              />
            </div>
            <div className="form-div">
              <label className="sign-label">{data.email}</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                autoCapitalize="none"
                className="sign-input"
                disabled={(!isGuestRegistration && userData.id) || registered}
              />
            </div>
            <div className={`form-div resident-field ${paid ? "is-disabled" : ""}`}>
              <label htmlFor="meeting-resident">{data.resident}</label>
              &nbsp;
              &nbsp;
              <input
                id="meeting-resident"
                type="checkbox"
                name="resident"
                checked={formData.resident}
                onChange={handleResidentChange}
                disabled={paid}
              />
              <br/>
              <br/>
            </div>

            {errorMsg && (
              <div className="form-div error-msg">
                <PiWarningCircleLight /> {errorMsg}
              </div>
            )}

            <button type="submit" className="sign-button" disabled={loading || paid}>
              {submitButtonText}
            </button>
          </form>
          
          {userData?.id ? null
          :
          <div>
            <hr/>

            <button type="button" className="sign-button" disabled={loading} onClick={() => navigate("/client/signin", { state: { from: memberRegisterFrom } })}>
              {data.signin}
            </button>
          </div>
          }
        </div>
      </div>
    </div>
  );
};

export default MeetingRegister;
